import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Paper, Typography, Stack, Button, Box, Autocomplete, CircularProgress, TextField } from "@mui/material";

import { ThemeContext } from "components/common/App";
import rpc, { buildRequest, signoutAndRedirectToIndex } from "lib/rpc";

export default function MaterialDetailsPage() {
  const theme = useContext(ThemeContext);
  const [material, setMaterial] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const { materialId } = useParams();

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState(null);

  useEffect(() => {
    theme.changeTheme("light-bg");
    window.scrollTo(0, 0); // Make sure to be on top of the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    rpc
      .post(
        "",
        buildRequest("admin.materials.details", {
          material_id: materialId,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          setMaterial(response.data.result);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching material details:", error);
        setLoading(false);
      });
  }, [materialId]);

  useEffect(() => {
    if (searchTerm.length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    rpc
      .post(
        "",
        buildRequest("admin.groups.list", {
          search: searchTerm,
          per_page: 10,
          page: 1,
          show_all: true, // Assuming you want to show all groups
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          setOptions(response.data.result.data);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching groups:", error);
        setLoading(false);
      });
  }, [searchTerm]);

  const handleAssignToGroup = () => {
    if (!selectedGroup) {
      alert("Please select a group to assign the material to.");
      return;
    }
    rpc
      .post(
        "",
        buildRequest("admin.materials.assign", {
          group_id: selectedGroup.id,
          material_id: materialId,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          setMaterial((prev) => ({
            ...prev,
            assignees: [...(prev.assignees || []), { group: selectedGroup }],
          }));
          setSearchTerm("");
          setSearchOpen(false);
          setOptions([]);
          setSelectedGroup(null);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      });
  };

  const handleUnassignFromGroup = (groupId) => {
    rpc
      .post(
        "",
        buildRequest("admin.materials.unassign", {
          group_id: groupId,
          material_id: materialId,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          setMaterial((prev) => ({
            ...prev,
            assignees: prev.assignees.filter((assignee) => assignee.group.id !== groupId),
          }));
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      });
  };

  return (
    <Box sx={{ padding: 2, width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
        <Typography variant="h4">{material ? material.name : "Material Details"}</Typography>
        {material && (
          <a href={material.download_link} target="_blank" rel="noopener noreferrer">
            Download Material
          </a>
        )}
      </Stack>

      <Box spacing={2} sx={{ marginBottom: 2 }}>
        <Paper sx={{ padding: 2 }}>
          <Stack spacing={1} padding={1}>
            <Typography variant="overline" padding={1} gutterBottom>
              Assign to Group
            </Typography>

            <Autocomplete
              id="asynchronous-demo"
              open={searchOpen}
              onOpen={() => {
                setSearchOpen(true);
              }}
              onClose={() => {
                setSearchOpen(false);
              }}
              onChange={(e, newValue) => setSelectedGroup(newValue)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.id} ${option.name}`}
              options={options}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  label="Search for a group by its Id or Name"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            <Button disabled={!selectedGroup} variant="contained" onClick={handleAssignToGroup}>
              {selectedGroup ? (
                <>
                  Assign[{materialId}] to {selectedGroup.id} {selectedGroup.name}
                </>
              ) : (
                <>Find a group</>
              )}
            </Button>
          </Stack>
        </Paper>
      </Box>

      {material &&
        material.assignees &&
        material.assignees.map((assignee, index) => (
          <Stack
            key={index}
            direction="column"
            spacing={2}
            alignItems="space-evenly"
            sx={{ width: "100%", marginBottom: 2 }}
          >
            <Paper sx={{ padding: 2, flexGrow: 1 }}>
              <Stack spacing={1} direction={"row"} justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {assignee.group.id} | {assignee.group.name}
                </Typography>
                <Button variant="outlined" color="error" onClick={() => handleUnassignFromGroup(assignee.group.id)}>
                  Unassign
                </Button>
              </Stack>
            </Paper>
          </Stack>
        ))}
    </Box>
  );
}
